import React, { useContext, useMemo, useCallback, useState, useEffect } from "react";
import { Form, useForm, useFormState } from "react-final-form";
import arrayMutators from "final-form-arrays";
import { Checkbox, SelectCheckbox, AutocompleteSelectCheckboxField } from "components/Form/Field";
import usePromise from "helpers/usePromise";
import produitDataProvider from "dataProvider/Produit";
import typeMarqueDataProvider, { TypeMarqueInterface } from "dataProvider/TypeMarque";
import secteurDataProvider, { SecteurInterface } from "dataProvider/Secteur";
import constitutantStdDataProvider, { ConstituantStdInterface } from "dataProvider/ConstituantStd";
import familleDataProvider, { FamilleInterface } from "dataProvider/Famille";
import anneeSecteurDataProvider, { AnneeInterface } from "dataProvider/AnneeSecteur";
import { Options } from "ui/Form/Element/Select";
import { IntlContext, ListContext } from "context";
import FilterActions from "./FilterActions";
import { Modal } from "ui/Modal";
import Tooltip from "ui/Form/Element/Tooltip";
import { matomoPush } from "../../Produit/helpers";


export interface FiltersOptions {
    secteurs: OptionsSelectCheckBoxProps[];
    familles: OptionsSelectCheckBoxProps[];
    typeMarques: OptionsSelectCheckBoxProps[];
    nutriments: OptionsSelectCheckBoxProps[];
    annees: OptionsSelectCheckBoxProps[];
    displayType: Options[];
}

export interface FiltersFormProps {
    options: FiltersOptions;
    setActiveFilters: (params: any) => void;
}

export interface Props {
    setActiveFilters: (params: any) => void;
    setFiltersMax?: (params: any) => void;
    filtersMax?: any;
}

export enum displayView {
    STATS = "stats",
    DATA = "data",
}

const FiltersForm: React.FC<FiltersFormProps> = ({ options, setActiveFilters }) => {
    const form = useForm();
    const { values } = useFormState();
    const intl = useContext(IntlContext);
    const { setActiveFetch } = useContext(ListContext);
    const [familleOptions, setFamilleOptions] = useState<OptionsSelectCheckBoxProps[]>([]);
    const [hasSelectedFilter, setHasSelectedFilter] = useState<boolean>(false);

    useMemo(() => {
        if (options?.familles && familleOptions.length === 0) {
            setFamilleOptions(options.familles);
        }
        // eslint-disable-next-line
    }, [options?.familles]);

    const resetFamilleOptions = useCallback(() => {
        form.change("filter_famille", []);
        setFamilleOptions(options.familles);
    }, [options.familles, form]);

    const handleOnReset = useCallback(
        (event: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
            form.batch(() => {
                resetFamilleOptions();
                setActiveFilters(undefined);
                form.reset({ filter_display_type: [displayView.STATS, displayView.DATA] });
            });
            setActiveFetch(false);
            event.preventDefault();
        },
        [form, setActiveFetch, resetFamilleOptions, setActiveFilters]
    );

    const onFilterChange = useCallback(
        (event: React.ChangeEvent<HTMLSelectElement>, filterName: string) => {
            const value = event.target.value;
            const currentValues = Object.assign({}, values);
            let selectedValues = currentValues?.[filterName] || [];
            if (selectedValues.includes(value)) {
                selectedValues = selectedValues.filter((v: any) => v !== value);
            } else {
                selectedValues.push(value);
            }
            form.change(
                filterName,
                selectedValues.filter((clear: any) => {
                    return clear !== "";
                })
            );
            return selectedValues;
        },
        [values, form]
    );

    const updateFamilleOptionsBySecteur = async (selectedValues: string[]) => {
        try {
            const data = await familleDataProvider.autocomplete({
                filter_secteur_id: selectedValues.length ? selectedValues.join("|") : undefined,
            });

            setFamilleOptions(
                (data?.data?.data || []).map((famille: HashMap<any>) => ({
                    label: `${famille.name} (${famille.secteur_name})`,
                    value: "" + famille.famille_id,
                }))
            );
        } catch (err) {
            throw err;
        }
    };

    const onSecteurChange = useCallback(
        async (event: React.ChangeEvent<HTMLSelectElement>) => {
            const selectedValues = onFilterChange(event, "filter_secteur");
            updateFamilleOptionsBySecteur(selectedValues);
        },
        [onFilterChange]
    );

    const onFamilleChange = useCallback(
        (event: React.ChangeEvent<HTMLSelectElement>) => {
            onFilterChange(event, "filter_famille");
        },
        [onFilterChange]
    );

    const onTypeMarqueChange = useCallback(
        (event: React.ChangeEvent<HTMLSelectElement>) => {
            onFilterChange(event, "filter_type_marque");
        },
        [onFilterChange]
    );

    const onNutrimentChange = useCallback(
        (event: React.ChangeEvent<HTMLSelectElement>) => {
            onFilterChange(event, "filter_nutriment");
        },
        [onFilterChange]
    );

    const onDisplayTypeChange = useCallback(
        (event) => {
            onFilterChange(event, "filter_display_type");
        },
        [onFilterChange]
    );

    const onAnneeChange = useCallback(
        (event: React.ChangeEvent<HTMLSelectElement>) => {
            onFilterChange(event, "filter_annee");
        },
        [onFilterChange]
    );

    const handleSelectAllSecteur = useCallback(() => {
        resetFamilleOptions();
    }, [resetFamilleOptions]);

    const handleUnselectAllSecteur = useCallback(() => {
        resetFamilleOptions();
    }, [resetFamilleOptions]);

    // const handleSelectAllFamille = useCallback(() => {
    // }, []);

    // const handleUnselectAllFamille = useCallback(() => {
    // }, []);

    const downloadAnneFile = useCallback(() => {
        produitDataProvider
            .downloadFileAnnees()
            .then((data: any) => {
                const link = document.createElement("a");
                link.href = URL.createObjectURL(data.content);
                link.download = data.name;
                link.click();
                link.parentNode?.removeChild(link);
            })
            .catch((err) => {
                throw err;
            });
    }, []);

    useEffect(() => {
        const filterNames = [
            "filter_secteur",
            "filter_famille",
            "filter_type_marque",
            "filter_nutriment",
            "filter_annee",
        ];
        setHasSelectedFilter(
            filterNames.some((filterName: string) => {
                return (values?.[filterName] || []).length > 0;
            })
        );
    }, [values]);

    return (
        <React.Fragment>
            <fieldset className="search-fieldset search-fieldset--open " style={{ display: "flex" }}>
                <div className="filters_stats">
                    {/* Elements de filtrage */}
                    <div className="filters_stats--filters">
                        <div className="searchbar-select-group">
                            {/* FILTRE SECTEUR */}
                            <SelectCheckbox
                                fieldGroupProps={{
                                    className: "field field-stats",
                                }}
                                inputProps={{
                                    name: "filter_secteur",
                                    id: "filter_secteur",
                                    placeholder: intl.formatMessage("produit.label.secteur") + " **",
                                    options: options.secteurs,
                                    readOnly: true,
                                    onChange: onSecteurChange,
                                }}
                                type="select"
                                displaySelected={"stats.filters.secteur.selected"}
                                multiple={true}
                                listHeader={
                                    <FilterActions
                                        onSelectAll={handleSelectAllSecteur}
                                        onUnselectAll={handleUnselectAllSecteur}
                                        id="filter_secteur_all"
                                        filterName="filter_secteur"
                                        options={options.secteurs}
                                    />
                                }
                                adormentRight={<Tooltip value={intl.formatMessage("stats.filters.help.secteur")} />}
                            />

                            {/* FILTRE FAMILLE */}
                            <AutocompleteSelectCheckboxField
                                fieldGroupProps={{
                                    className: "field field-stats",
                                }}
                                inputProps={{
                                    name: "filter_famille",
                                    id: "filter_famille",
                                    placeholder: intl.formatMessage("produit.label.famille") + " ***",
                                    options: familleOptions,
                                    onChange: onFamilleChange,
                                }}
                                type="select"
                                multiple={true}
                                displaySelected={"stats.filters.famille.selected"}
                                listHeader={
                                    <FilterActions
                                        //onSelectAll={handleSelectAllFamille}
                                        //onUnselectAll={handleUnselectAllFamille}
                                        id="filter_famille_all"
                                        filterName="filter_famille"
                                        options={familleOptions}
                                    />
                                }
                                adormentRight={<Tooltip value={intl.formatMessage("stats.filters.help.famille")} />}
                            />
                        </div>
                        <div className={`searchbar-select-group `}>
                            {/* FILTRE TYPE DE MARQUE (segment) */}
                            <SelectCheckbox
                                fieldGroupProps={{
                                    className: "field field-stats",
                                }}
                                inputProps={{
                                    name: "filter_type_marque",
                                    id: "filter_type_marque",
                                    placeholder: intl.formatMessage("stats.filters.type_marque"),
                                    options: options.typeMarques,
                                    readOnly: true,
                                    onChange: onTypeMarqueChange,
                                }}
                                type="select"
                                displaySelected={"stats.filters.type_marque.selected"}
                                multiple={true}
                                listHeader={<FilterActions id="filter_type_marque_all" filterName="filter_type_marque" options={options.typeMarques} />}
                                adormentRight={<Tooltip value={intl.formatMessage("stats.filters.help.type_marque")} />}
                            />

                            {/* FILTRE NUTRIMENT (constituant standard) */}
                            <AutocompleteSelectCheckboxField
                                fieldGroupProps={{
                                    className: "field field-stats",
                                }}
                                inputProps={{
                                    name: "filter_nutriment",
                                    id: "filter_nutriment",
                                    placeholder: intl.formatMessage("stats.filters.nutriment") + " *",
                                    options: options.nutriments,
                                    onChange: onNutrimentChange,
                                }}
                                type="select"
                                displaySelected={"stats.filters.nutriment.selected"}
                                multiple={true}
                                listHeader={<FilterActions id="filter_nutriment_all" filterName="filter_nutriment" options={options.nutriments} />}
                                adormentRight={<Tooltip value={intl.formatMessage("stats.filters.help.nutriment")} />}
                            />
                        </div>
                        <div className={`searchbar-select-group `} style={{ alignItems: "center" }}>
                            {/* FILTRE ANNEE (Voir import)  */}
                            <SelectCheckbox
                                fieldGroupProps={{
                                    className: "field field-stats",
                                }}
                                inputProps={{
                                    name: "filter_annee",
                                    id: "filter_annee",
                                    placeholder: intl.formatMessage("stats.filters.annee") + " *",
                                    options: options.annees,
                                    readOnly: true,
                                    onChange: onAnneeChange,
                                }}
                                displaySelected={"stats.filters.annee.selected"}
                                type="select"
                                multiple={true}
                                listHeader={<FilterActions id="filter_annee_all" filterName="filter_annee" options={options.annees} />}
                                adormentRight={<Tooltip value={intl.formatMessage("stats.filters.help.annee")} />}
                            />
                            <button
                                type="button"
                                className="cta cta--link"
                                onClick={downloadAnneFile}
                                title={intl.formatMessage("stats.filters.annee.file")}
                            >
                                {intl.formatMessage("stats.filters.annee.file")}
                            </button>
                        </div>

                        {/* SELECTION ONGLET Statistiques descriptives / Visualisation des données */}
                        <div className={`searchbar-select-group `} style={{ alignItems: "center", marginBottom: "10px" }}>
                            <Checkbox
                                inputProps={{
                                    name: "filter_display_type",
                                    options: options.displayType,
                                    onChange: onDisplayTypeChange,
                                }}
                                fieldGroupProps={{
                                    className: "popup-checkbox-all",
                                }}
                            />
                        </div>
                        <div className="search-legend">
                            <p className="search-legend__item">{intl.formatMessage("stats.filters.legend.one_star")}</p>
                            <p className="search-legend__item">{intl.formatMessage("stats.filters.legend.two_stars")}</p>
                            <p className="search-legend__item">{intl.formatMessage("stats.filters.legend.three_stars")}</p>
                        </div>
                    </div>

                    {/* Actions */}
                    <div className="filters_stats--actions">
                        {/* BOUTON RECHERCHER */}
                        <button
                            className="cta cta--primary"
                            id="search-button"
                            title={intl.formatMessage("generic.selection_validate")}
                            style={{  }}
                        >
                            {intl.formatMessage("generic.validate")}
                        </button>
                        {/* BOUTON REINITIALISER */}
                        {hasSelectedFilter && (
                            <button
                                type="button"
                                className="cta cta--link filter-reset"
                                onClick={(e) => handleOnReset(e)}
                                style={{ float: "none" }}
                            >
                                <i className="icon icon-cross"></i> {intl.formatMessage("generic.reset")}
                            </button>
                        )}
                    </div>
                </div>
            </fieldset>
        </React.Fragment>
    );
};

const FiltersStats: React.FC<Props> = ({ setActiveFilters, setFiltersMax, filtersMax }) => {
    const { changeFetcherParams, setActiveFetch } = useContext(ListContext);
    const intl = useContext(IntlContext);
    const [message, setMessage] = useState<string[]>([]);

    const secteurData = usePromise(secteurDataProvider.all);
    const familleData = usePromise(familleDataProvider.all);
    const typeMarqueData = usePromise(typeMarqueDataProvider.all);
    const constituantStdData = usePromise(constitutantStdDataProvider.all);
    const anneeData = usePromise(anneeSecteurDataProvider.all);

    const formatValue = useCallback((value) => {
        return "" + value;
    }, []);

    const secteurOptions: Array<OptionsSelectCheckBoxProps> = useMemo(
        () => {
            const elem = (secteurData.data || []).reduce((memo: Array<OptionsSelectCheckBoxProps>, secteur: SecteurInterface) => {
                memo.push({
                    label: secteur.name,
                    value: formatValue(secteur.secteur_id),
                });
                return memo;
            }, []);
            !!setFiltersMax && filtersMax.secteur !== elem.length && setFiltersMax({...filtersMax, secteur: elem.length});
            return elem;
        },
        [secteurData, formatValue, setFiltersMax, filtersMax]
    );

    const familleOptions: Array<OptionsSelectCheckBoxProps> = useMemo(
        () => {
            const elem = (familleData.data || []).reduce((memo: Array<OptionsSelectCheckBoxProps>, famille: FamilleInterface) => {
                memo.push({
                    label: `${famille.name} (${famille.secteur_name})`,
                    value: formatValue(famille.id),
                });
                return memo;
            }, []);
            !!setFiltersMax && filtersMax.famille !== elem.length && setFiltersMax({...filtersMax, famille: elem.length});
            return elem;
        },
        [familleData, formatValue, setFiltersMax, filtersMax]
    );

    const typeMarqueOptions: Array<OptionsSelectCheckBoxProps> = useMemo(
        () => {
            const elem = (typeMarqueData.data || []).reduce(
                (memo: Array<OptionsSelectCheckBoxProps>, typeMarque: TypeMarqueInterface) => {
                    if (typeMarque.libel.length > 1) {
                        memo.push({
                            label: typeMarque.libel,
                            value: formatValue(typeMarque.id),
                        });
                    }
                    return memo;
                }, []);
            !!setFiltersMax && filtersMax.typeMarque !== elem.length && setFiltersMax({...filtersMax, typeMarque: elem.length});
            return elem;
        },
        [typeMarqueData, formatValue, setFiltersMax, filtersMax]
    );

    const constituantOptions: Array<OptionsSelectCheckBoxProps> = useMemo(
        () => {
            const elem = (constituantStdData.data || []).reduce(
                (memo: Array<OptionsSelectCheckBoxProps>, constituant: ConstituantStdInterface) => {
                    memo.push({
                        label: constituant.libel,
                        value: formatValue(constituant.id),
                    });
                    return memo;
                }, []);
            !!setFiltersMax && filtersMax.nutriment !== elem.length && setFiltersMax({...filtersMax, nutriment: elem.length});
            return elem;
        },
        [constituantStdData, formatValue, setFiltersMax, filtersMax]
    );

    const anneeOptions: OptionsSelectCheckBoxProps[] = useMemo(
        () => {
            const elem = (anneeData.data || []).reduce((memo: OptionsSelectCheckBoxProps[], annee: AnneeInterface) => {
                memo.push({
                    label: annee.numero_etude,
                    value: formatValue(annee.numero_suivi),
                });
                return memo;
            }, []);
            !!setFiltersMax && filtersMax.annee !== elem.length && setFiltersMax({...filtersMax, annee: elem.length});
            return elem;
        },
        [anneeData, formatValue, setFiltersMax, filtersMax]
    );

    const displayTypeOptions: Options[] = [
        { value: displayView.STATS, label: intl.formatMessage("stats.filters.display_stats") },
        { value: displayView.DATA, label: intl.formatMessage("stats.filters.display_data") },
    ];

    const initialValues = useMemo(() => {
        return {
            filter_secteur: [],
            filter_famille: [],
            filter_type_marque: [],
            filter_nutriment: [],
            filter_annee: [],
            filter_display_type: [displayView.STATS, displayView.DATA],
        };
    }, []);

    const formValidate = useCallback(
        (values: any) => {
            const message = [];

            if (!(values?.filter_secteur || []).length && !(values?.filter_famille || []).length) {
                message.push(
                    `${intl.formatMessage("produit.label.secteur")} ${intl.formatMessage(
                        "stats.filters.or"
                    )} ${intl.formatMessage("produit.label.famille")}`
                );
            }
            if (!(values?.filter_nutriment || []).length) {
                message.push(intl.formatMessage("stats.filters.nutriment"));
            }
            if (!(values?.filter_annee || []).length) {
                message.push(intl.formatMessage("stats.filters.annee"));
            }
            if (!(values?.filter_display_type || []).length) {
                message.push(intl.formatMessage("stats.filters.stats_data"));
            }

            setMessage(message);

            return !message.length;
        },
        [intl]
    );

    // eslint-disable-next-line
    const getValue = (filterName: string, filterValue: any) => {
        switch (filterName) {
            case "filter_secteur":
                return secteurOptions.filter((currentSecteur) => filterValue === currentSecteur.value).shift();
            case "filter_famille":
                return familleOptions.filter((current) => filterValue === current.value).shift();
            case "filter_type_marque":
                return typeMarqueOptions.filter((current) => filterValue === current.value).shift();
            case "filter_nutriment":
                return constituantOptions.filter((current) => filterValue === current.value).shift();
            case "filter_annee":
                return anneeOptions.filter((current) => filterValue === current.value).shift();
        }
    }

    const onSubmit = useCallback(
        (values: any) => {
            if (!formValidate(values)) {
                return;
            }

            const modal = {
                filter_annee: [], filter_display_type: [], filter_famille: [], filter_nutriment: [], filter_secteur: [], filter_type_marque: []
            } as FiltersStatsInterface;

            let matomo = {};
            Object.entries(values).forEach((filterValues: any[]) => {
                let name: string = filterValues.shift(),
                    filtersVal: any[] = filterValues.shift() || [];

                matomo = {
                    ...matomo,
                    [name]: filtersVal?.map((v: any) => getValue(name, v)?.label),
                };
                return filtersVal?.map((v: any) => getValue(name, v)?.label);
            });
            const params = Object.assign({}, modal, values, { matomo: matomo });

            setActiveFilters(params);
            setActiveFetch(true);
            changeFetcherParams(params);
            matomoPush([
                [
                    "trackEvent",
                    "Visualisation des données et statistiques descriptives",
                    "Recherche",
                    JSON.stringify(matomo),
                ],
            ]);
        },
        [changeFetcherParams, setActiveFetch, formValidate, setActiveFilters, getValue]
    );

    const handleMessageModalClose = useCallback(() => {
        setMessage([]);
    }, [setMessage]);

    return (
        <Form
            autoComplete="off"
            initialValues={initialValues}
            mutators={{ ...arrayMutators }}
            onSubmit={onSubmit}
            render={({ form, handleSubmit }) => {
                return (
                    <form onSubmit={handleSubmit} id="pageup">
                        <FiltersForm
                            options={{
                                secteurs: secteurOptions,
                                familles: familleOptions,
                                typeMarques: typeMarqueOptions,
                                annees: anneeOptions,
                                nutriments: constituantOptions,
                                displayType: displayTypeOptions,
                            }}
                            setActiveFilters={setActiveFilters}
                        />
                        <Modal
                            show={!!message.length}
                            onClose={handleMessageModalClose}
                            title={`${intl.formatMessage("stats.filters.validate.message")}`}
                        >
                            {message.map((m: string, key: number) => (
                                <div key={key}>- {m}</div>
                            ))}
                        </Modal>
                    </form>
                );
            }}
        />
    );
};

export default FiltersStats;
